import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Twitter, Send, Clock, CheckCircle2, XCircle, ShieldAlert, KeyRound, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

export default function AdminSocialX() {
  const [data, setData] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/social/x/overview").then((r) => {
      setData(r.data);
      setEnabled(r.data.enabled);
      setHours(r.data.interval_hours);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(ok || r.data?.message || "Done");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="font-mono text-[10px] text-zinc-600 animate-pulse p-4">Loading X Autoposter…</p>;

  return (
    <section data-testid="admin-socialx-section" className="flex flex-col gap-4">
      <div className="border border-white/10 bg-black/40">
        <div className="border-b border-white/10 px-6 py-4">
          <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
            <Twitter className="w-4 h-4 text-nc-blue" /> X / Twitter Autoposter — Fab Catalog
          </h2>
          <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
            Posts ONLY your own Fab listings (the admin-curated catalog in Fab Store) on a schedule — round-robin, one product per post, with thumbnail.
            Hard-capped at {data.caps.per_day} posts/24h and {data.caps.per_30d} posts/30d to protect X's free tier. It can never post another creator's work.
          </p>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Status + config */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span data-testid="xkeys-status" className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2.5 py-1.5 flex items-center gap-1.5 ${data.keys_configured ? "text-nc-green border-nc-green/40 bg-nc-green/5" : "text-red-400 border-red-500/40 bg-red-500/5"}`}>
                <KeyRound className="w-3 h-3" /> API keys: {data.keys_configured ? "configured" : "missing"}
              </span>
              <span data-testid="xautopost-status" className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2.5 py-1.5 ${data.enabled ? "text-nc-green border-nc-green/40 bg-nc-green/5" : "text-zinc-500 border-white/15"}`}>
                Autoposter: {data.enabled ? `on — every ${data.interval_hours}h` : "off"}
              </span>
              <span data-testid="xusage-status" className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500 border border-white/10 px-2.5 py-1.5">
                {data.posts_24h}/{data.caps.per_day} today · {data.posts_30d}/{data.caps.per_30d} this month
              </span>
            </div>

            {!data.keys_configured && (
              <div data-testid="xkeys-missing-note" className="border border-nc-orange/40 bg-nc-orange/5 p-4">
                <p className="font-mono text-[10px] tracking-[0.2em] text-nc-orange uppercase mb-2">Keys needed — 5 minutes at developer.x.com</p>
                <ol className="font-mono text-[10px] text-zinc-400 leading-relaxed list-decimal list-inside flex flex-col gap-1">
                  <li>Create a free project at developer.x.com → your app → Keys and Tokens</li>
                  <li>Set app permissions to <b className="text-zinc-200">Read and Write</b> (then regenerate tokens)</li>
                  <li>Send the dev team: API Key, API Secret, Access Token, Access Token Secret</li>
                </ol>
              </div>
            )}

            <div className="border border-white/10 bg-[#050505]/60 p-4 flex items-center gap-3 flex-wrap">
              <input type="checkbox" data-testid="xpost-enable-toggle" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-3.5 h-3.5 accent-[#2E6BFF]" />
              <span className="font-mono text-[10px] text-zinc-300">Auto-post every</span>
              <input data-testid="xpost-interval-input" type="number" min="4" max="168" className={`${inputCls} w-20 text-center`} value={hours} onChange={(e) => setHours(parseInt(e.target.value) || 24)} />
              <span className="font-mono text-[10px] text-zinc-400">hours</span>
              <button data-testid="xpost-config-save" disabled={busy} onClick={() => act(() => api.put("/admin/social/x/config", { enabled, interval_hours: hours }))} className="ml-auto font-mono text-[9px] uppercase px-3 py-1.5 border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 transition-colors">Save</button>
            </div>
            {data.last_post_at && <p className="font-mono text-[9px] text-zinc-600">Last posted: {String(data.last_post_at).slice(0, 16).replace("T", " ")} UTC</p>}

            {/* Kill-the-rogue-app guide */}
            <div data-testid="rogue-app-guide" className="border border-red-500/30 bg-red-500/5 p-4">
              <p className="font-mono text-[10px] tracking-[0.2em] text-red-400 uppercase mb-2 flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5" /> Stop the rogue poster (do this once)</p>
              <ol className="font-mono text-[10px] text-zinc-400 leading-relaxed list-decimal list-inside flex flex-col gap-1">
                <li>X → <b className="text-zinc-200">Settings and privacy → Security and account access → Apps and sessions → Connected apps</b></li>
                <li>Revoke anything you don't recognise (Zapier, Make, Buffer, dlvr.it, IFTTT, "Fab feed" bots…)</li>
                <li>Also check those tools' own dashboards for a Fab/RSS → X automation and pause it</li>
              </ol>
              <p className="font-mono text-[9px] text-zinc-600 mt-2">This platform has never posted to X — the random Fab posts come from a connected third-party app. Revoking it here stops them instantly.</p>
            </div>
          </div>

          {/* Next-up preview + history */}
          <div className="flex flex-col gap-4">
            {data.next_up ? (
              <div data-testid="xpost-next-up" className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-3">
                <p className={labelCls}>Next up in rotation ({data.catalog_size} listings)</p>
                <div className="flex items-center gap-3">
                  {data.next_up.thumbnail && <img src={data.next_up.thumbnail} alt="" className="w-12 h-12 object-cover border border-white/10" />}
                  <div>
                    <p className="font-sans font-semibold text-sm text-zinc-100">{data.next_up.name}</p>
                    <p className="font-mono text-[10px] text-nc-green">{data.next_up.price}{data.next_up.discount ? ` (${data.next_up.discount})` : ""}</p>
                  </div>
                </div>
                <pre data-testid="xpost-preview-text" className="font-mono text-[10px] text-zinc-400 leading-relaxed whitespace-pre-wrap border border-white/10 bg-black/40 p-3">{data.next_up.preview_text}</pre>
                <button data-testid="xpost-now-button" disabled={busy || !data.keys_configured} onClick={() => act(() => api.post("/admin/social/x/post-now"))} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 flex items-center justify-center gap-2 transition-colors">
                  <Send className="w-3.5 h-3.5" /> Post This to X Now
                </button>
              </div>
            ) : (
              <p data-testid="xpost-catalog-empty" className="font-mono text-[10px] text-zinc-600 border border-white/10 p-4">Fab catalog is empty — add listings in Admin → Fab Store first.</p>
            )}

            <div className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-2">
              <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2"><Clock className="w-3 h-3" /> Post History</p>
              {data.recent_posts.length === 0 && <p data-testid="xpost-history-empty" className="font-mono text-[10px] text-zinc-600">No posts sent yet.</p>}
              {data.recent_posts.map((p, i) => (
                <div key={p.id} data-testid={`xpost-post-${i + 1}`} className="flex items-center justify-between gap-3 border-b border-white/5 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {p.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-nc-green shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                    <div className="min-w-0">
                      {p.ok && p.tweet_id ? (
                        <a href={`https://x.com/i/web/status/${p.tweet_id}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-nc-blue hover:underline truncate block">{p.product_name}</a>
                      ) : (
                        <span className="font-mono text-[10px] text-zinc-300 truncate block">{p.product_name}</span>
                      )}
                      {p.error && <span className="font-mono text-[9px] text-red-400 truncate block">{p.error}</span>}
                    </div>
                  </div>
                  <span className="font-mono text-[9px] text-zinc-500 shrink-0">{p.auto ? "auto // " : ""}{String(p.created_at).slice(5, 16).replace("T", " ")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
